<template>
  <loader :task="task" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Account, ReconnectRequest } from '@/social/types';
import { HasTaskMixin } from '@/mixins';
import Loader from '@/components/loading/AsyncLinearProgress.vue';
import { SocialAccountDeleted } from '@/messaging/events';
import {
  AccountReconnected,
  ReconnectRequestCanceled,
  ReconnectRequestCreated,
  ReconnectRequestEvent,
  RemindersDisabled,
  RemindersEnabled,
} from '@/social/account/action/reconnect';
import { AccountAdded } from '@/social/account';
import { PostImportCreated } from '@/social/post/import';
import { PostImportSupportRequested } from '@/social/post/import/support';
import eventBus from '@/messaging/event-bus';
import repository from './account-repository';

interface Data {
  task: Promise<Account[]>|undefined,
}

export default defineComponent({
  emits: ['loaded', 'update:modelValue'],
  mixins: [HasTaskMixin],
  components: {
    Loader,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Array as () => Account[],
      default: () => [],
    },
  },
  data(): Data {
    return {
      task: undefined,
    };
  },
  watch: {
    clientId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  methods: {
    load() {
      this.task = repository.findAll(this.clientId);
      this.task.then((accounts) => {
        this.$emit('update:modelValue', accounts);
        this.$emit('loaded', accounts);
      });
    },
    onAdded(event: AccountAdded): void {
      const { account } = event;
      if (account.clientId !== this.clientId) return;
      this.$emit('update:modelValue', [...this.modelValue, account]);
      this.$emit('loaded', [...this.modelValue, account]);
    },
    set(account: Account): void {
      const index = this.modelValue.findIndex(sa => sa.id === account.id);
      if (index >= 0) {
        const accounts = [...this.modelValue];
        accounts.splice(index, 1, account);
        this.$emit('update:modelValue', accounts);
        this.$emit('loaded', accounts);
      }
    },
    onDeleted(event: SocialAccountDeleted) {
      const { id } = event.socialAccount;
      const index = this.modelValue.findIndex(sa => sa.id === id);
      if (index >= 0) {
        const accounts = [...this.modelValue];
        accounts.splice(index, 1);
        this.$emit('update:modelValue', accounts);
        this.$emit('loaded', accounts);
      }
    },
    setReconnectRequest(reconnectRequest: ReconnectRequest): void {
      const { accountId } = reconnectRequest;
      const account = this.modelValue.find(sa => sa.id === accountId);
      if (account) {
        this.set({ ...account, reconnectRequest });
      }
    },
    onReconnectRequestCreated(event: ReconnectRequestCreated): void {
      this.setReconnectRequest(event.reconnectRequest);
    },
    onReconnectRequestCanceled(event: ReconnectRequestCanceled): void {
      const { accountId } = event.reconnectRequest;
      const account = this.modelValue.find(sa => sa.id === accountId);
      if (account) {
        this.set({ ...account, reconnectRequest: null });
      }
    },
    onReconnected(event: AccountReconnected): void {
      this.set(event.account);
    },
    onReconnectRequestUpdated(event: ReconnectRequestEvent): void {
      this.setReconnectRequest(event.reconnectRequest);
    },
    async onPostImportCreated(event: PostImportCreated): Promise<void> {
      const { accountId } = event.postImport;
      const account = this.modelValue.find(sa => sa.id === accountId);
      if (account) {
        // When post import is created it can result in many changes to
        // an account so it's easier to just load the latest account
        const latestAccount = await repository.find(account.id);
        this.set(latestAccount);
      }
    },
  },
  created() {
    eventBus.$on(AccountAdded.eventName, this.onAdded);
    eventBus.$on(SocialAccountDeleted.eventName, this.onDeleted);
    eventBus.$on(ReconnectRequestCreated.eventName, this.onReconnectRequestCreated);
    eventBus.$on(ReconnectRequestCanceled.eventName, this.onReconnectRequestCanceled);
    eventBus.$on(AccountReconnected.eventName, this.onReconnected);
    eventBus.$on(RemindersEnabled.eventName, this.onReconnectRequestUpdated);
    eventBus.$on(RemindersDisabled.eventName, this.onReconnectRequestUpdated);
    eventBus.$on(PostImportSupportRequested.eventName, this.onReconnectRequestUpdated);
    eventBus.$on(PostImportCreated.eventName, this.onPostImportCreated);
  },
  beforeUnmount() {
    eventBus.$off(AccountAdded.eventName, this.onAdded);
    eventBus.$off(SocialAccountDeleted.eventName, this.onDeleted);
    eventBus.$off(ReconnectRequestCreated.eventName, this.onReconnectRequestCreated);
    eventBus.$off(ReconnectRequestCanceled.eventName, this.onReconnectRequestCanceled);
    eventBus.$off(AccountReconnected.eventName, this.onReconnected);
    eventBus.$off(RemindersEnabled.eventName, this.onReconnectRequestUpdated);
    eventBus.$off(RemindersDisabled.eventName, this.onReconnectRequestUpdated);
    eventBus.$off(PostImportSupportRequested.eventName, this.onReconnectRequestUpdated);
    eventBus.$off(PostImportCreated.eventName, this.onPostImportCreated);
  },
});
</script>
